import { useState, useEffect } from 'react';
import { Portal } from './Portal';

interface InteractiveCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, type: 'OUTBOUND' | 'INBOUND') => void;
  initialName?: string;
  initialType?: 'OUTBOUND' | 'INBOUND';
  title?: string;
  /** Quando definido, esconde a escolha de tipo e usa apenas este tipo (ex: página Fluxos / Bot) */
  forceType?: 'OUTBOUND' | 'INBOUND';
}

export function InteractiveCampaignModal({
  isOpen,
  onClose,
  onSave,
  initialName = '',
  initialType = 'OUTBOUND',
  title = 'Nova Automação',
  forceType,
}: InteractiveCampaignModalProps) {
  const effectiveType = forceType ?? initialType;
  const [name, setName] = useState(initialName);
  const [type, setType] = useState<'OUTBOUND' | 'INBOUND'>(effectiveType);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setType(forceType ?? initialType);
    }
  }, [isOpen, initialName, initialType, forceType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    onSave(name, forceType ?? type);
    handleClose();
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-card max-w-md w-full p-6 z-10">
            <h2 className="text-xl font-semibold text-ui-text mb-4">{title}</h2>

            <form onSubmit={handleSubmit}>
              {/* Tipo: Bot ou Campanha (oculto quando forceType está definido) */}
              {!forceType && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-ui-text mb-3">
                    Tipo de Fluxo
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setType('OUTBOUND')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        type === 'OUTBOUND'
                          ? 'border-brand-primary bg-brand-primary/5'
                          : 'border-ui-border hover:border-brand-primary/50'
                      }`}
                    >
                      <div className="text-2xl mb-2">📤</div>
                      <div className="font-semibold text-ui-text text-sm">Campanha</div>
                      <div className="text-xs text-ui-sub mt-1">
                        Você dispara manualmente
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setType('INBOUND')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        type === 'INBOUND'
                          ? 'border-brand-primary bg-brand-primary/5'
                          : 'border-ui-border hover:border-brand-primary/50'
                      }`}
                    >
                      <div className="text-2xl mb-2">🤖</div>
                      <div className="font-semibold text-ui-text text-sm">Bot</div>
                      <div className="text-xs text-ui-sub mt-1">
                        Responde automaticamente
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Nome */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-ui-text mb-2">
                  {(forceType ?? type) === 'INBOUND' ? 'Nome do Bot' : 'Nome da Campanha'}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={(forceType ?? type) === 'INBOUND' ? 'Ex: Bot de Atendimento' : 'Ex: Fluxo de Boas-vindas'}
                  className="w-full px-4 py-2 border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                  required
                  autoFocus
                />
                <p className="mt-2 text-xs text-ui-sub">
                  {(forceType ?? type) === 'INBOUND'
                    ? '🤖 Configure as palavras-chave e conexões no nó Início para ativar o bot'
                    : '💡 Você configurará as conexões WhatsApp e categorias no nó Início após criar a campanha'
                  }
                </p>
              </div>

              {/* Ações */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-ui-sub hover:text-ui-text transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-brand-primary text-white rounded-xl hover:opacity-90 transition-opacity"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Portal>
  );
}
